/** Frontend-only display mappings for analysis field values. */

export const REQUESTED_BOOLEAN_FIELD_IDS = new Set([
  "design.application_security.api_discovery_required",
  "design.application_security.bot_manager_required",
  "design.application_security.client_side_protection_required",
  "design.application_security.pci_certification_capabilities_required",
  "design.application_security.l7_ddos_requested",
  "design.application_security.llm_firewall_required",
  "design.ddos.cloud.dns_ddos_protection_required",
  "design.ddos.cloud.https_flood_protection_required",
  "design.ddos.onprem.dns_ddos_protection_required",
  "design.ddos.onprem.https_flood_protection_required",
  "sti.implementation.vendor_implementation_required",
  "sti.implementation.attack_tests_required",
  "sti.implementation.vendor_engineer_certification_required",
  "sti.implementation.partner_engineer_certification_required",
  "sti.training.certified_or_knowledge_transfer",
]);

export const DEPLOYMENT_MODEL_FIELD_ID = "design.application_security.deployment_model_cloud_onprem_both";

const BOT_MANAGER_FIELD_IDS = new Set([
  "design.application_security.bot_manager_required",
  "design.application_security.sophisticated_bot_requirement_1",
  "design.application_security.sophisticated_bot_requirement_2",
]);

export function isRequestedBooleanField(fieldId) {
  return REQUESTED_BOOLEAN_FIELD_IDS.has(fieldId);
}

export function isBotManagerCombinedField(fieldId) {
  return BOT_MANAGER_FIELD_IDS.has(fieldId);
}

export function formatRequestedBoolean(value, language = "en") {
  if (value === true) {
    if (language === "es") return "Sí se solicita";
    if (language === "pt") return "Solicitado";
    return "Requested";
  }
  if (value === false) {
    if (language === "es") return "No se solicita";
    if (language === "pt") return "Não solicitado";
    return "Not requested";
  }
  return null;
}

export function formatDeploymentModel(value, language = "en") {
  const es = language === "es";
  const pt = language === "pt";
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  const andWord = es ? " y " : pt ? " e " : " and ";
  if (normalized === "both") return `Integrated WAF${andWord}Cloud WAF`;
  if (normalized === "on-premise" || normalized === "onprem" || normalized === "on-prem") {
    return "Integrated WAF / On-prem WAF";
  }
  if (normalized === "cloud") return "Cloud WAF";
  return String(value);
}

export function isPowerSupplyField(fieldId) {
  return typeof fieldId === "string" && /\.power_supply$/.test(fieldId);
}

export function formatPowerSupply(value, language = "en") {
  const es = language === "es";
  const pt = language === "pt";
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const type = value.type || value.feed || null;
    const redundant = value.redundant;
    if (redundant === true && type) {
      return es ? `Redundante · ${type}` : pt ? `Redundante · ${type}` : `Redundant · ${type}`;
    }
    if (redundant === false && type) {
      return es ? `No redundante · ${type}` : pt ? `Não redundante · ${type}` : `Non-redundant · ${type}`;
    }
    if (redundant === true) {
      return es
        ? "Redundante (tipo no especificado)"
        : pt
          ? "Redundante (tipo não especificado)"
          : "Redundant (type not specified)";
    }
    if (redundant === false) {
      return es
        ? "No redundante (tipo no especificado)"
        : pt
          ? "Não redundante (tipo não especificado)"
          : "Non-redundant (type not specified)";
    }
    if (type) {
      return es
        ? `${type} (redundancia no especificada)`
        : pt
          ? `${type} (redundância não especificada)`
          : `${type} (redundancy not specified)`;
    }
    return es
      ? "Requisito de fuente de poder"
      : pt
        ? "Requisito de fonte de alimentação"
        : "Power supply requirement";
  }
  return value == null ? "" : String(value);
}

/** Humanize common enum / snake_case tokens for display only. */
export function formatDisplayToken(value, language = "en") {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") {
    return formatRequestedBoolean(value, language) || String(value);
  }
  const raw = String(value).trim();
  if (!raw) return "—";
  const es = language === "es";
  const pt = language === "pt";
  const normalized = raw.toLowerCase().replace(/[-\s]+/g, "_");
  const enums = {
    on_prem: "On-prem",
    onprem: "On-prem",
    on_premise: "On-prem",
    cloud: "Cloud",
    hybrid: es || pt ? "Híbrido" : "Hybrid",
    both: es || pt ? "Ambos" : "Both",
    online: es ? "En línea" : "Online",
    onsite: es || pt ? "Presencial" : "Onsite",
    certified: es || pt ? "Certificado" : "Certified",
    knowledge_transfer: es
      ? "Transferencia de conocimiento"
      : pt
        ? "Transferência de conhecimento"
        : "Knowledge transfer",
  };
  if (enums[normalized]) return enums[normalized];
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/i.test(raw)) {
    return raw
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return raw;
}

const OBJECT_KEY_LABELS = {
  en: {
    protocol: "Protocol",
    requirement: "Requirement",
    component: "Component",
    availability: "Availability",
    phase: "Phase",
    timing: "Timing",
    sites: "Sites",
    site: "Site",
    milestone: "Milestone",
    date: "Date",
  },
  es: {
    protocol: "Protocolo",
    requirement: "Requisito",
    component: "Componente",
    availability: "Disponibilidad",
    phase: "Fase",
    timing: "Plazo",
    sites: "Sitios",
    site: "Sitio",
    milestone: "Hito",
    date: "Fecha",
  },
  pt: {
    protocol: "Protocolo",
    requirement: "Requisito",
    component: "Componente",
    availability: "Disponibilidade",
    phase: "Fase",
    timing: "Prazo",
    sites: "Sites",
    site: "Site",
    milestone: "Marco",
    date: "Data",
  },
};

export function formatObjectEntry(key, value, language = "en") {
  const labels = OBJECT_KEY_LABELS[language] || OBJECT_KEY_LABELS.en;
  const label = labels[key] || formatDisplayToken(key, language);
  return `${label}: ${formatDisplayToken(value, language)}`;
}

/** Soften repeated gap suffixes for display. */
export function formatGapListItem(text, language = "en") {
  const raw = String(text || "").trim();
  if (!raw) return { title: raw, detail: null };
  const suffixes = [
    /:\s*información no encontrada en las páginas aprobadas\.?$/i,
    /:\s*informação não encontrada nas páginas aprovadas\.?$/i,
    /:\s*information not found in the approved pages\.?$/i,
  ];
  for (const re of suffixes) {
    if (re.test(raw)) {
      const title = raw.replace(re, "").trim();
      const detail =
        language === "es"
          ? "No encontrado en páginas aprobadas"
          : language === "pt"
            ? "Não encontrado nas páginas aprovadas"
            : "Not found in approved pages";
      return { title, detail };
    }
  }
  return { title: raw, detail: null };
}

export function humanizeWarningToken(warning) {
  const raw = String(warning || "").trim();
  if (!raw) return raw;
  if (raw.startsWith("⚠")) {
    const inner = raw.replace(/^⚠\s*/, "");
    return `⚠ ${formatDisplayToken(inner, "en")}`;
  }
  return formatDisplayToken(raw, "en");
}
